import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { X, Search, ChevronRight, MessageSquare, Phone } from "lucide-react";
import { useEnquiry } from "@/components/site/EnquiryContext";
import { company, commercialSystems, residentialSystems } from "@/content/site";

const MenuIcon = () => (
  <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M3 6h18M3 12h18M3 18h18" />
  </svg>
);

// Every page, its sections and every system card. Ids match the element ids on each page.
const PAGES = [
  { key: "home", label: "Home", path: "/", sections: [["are-you", "Find your starting point"], ["about-summary", "About Alumicom"], ["featured", "Featured system"], ["solutions", "Residential and commercial"], ["projects", "On site and in the shop"], ["process", "How it works"], ["strengths", "Why Alumicom"], ["reviews", "Client reviews"], ["about", "Company details and product range"], ["contact", "Contact"]] },
  { key: "residential", label: "Residential", path: "/residential", sections: [["solutions", "Residential systems"], ["projects", "Finished projects"], ["process", "How it works"], ["questions", "Buying questions"], ["architects", "Architects and builders"], ["contact", "Contact"]] },
  { key: "commercial", label: "Commercial", path: "/commercial", sections: [["scope", "Project scope"], ["solutions", "Commercial systems"], ["projects", "Finished projects"], ["process", "Process"], ["documents", "Technical documents"], ["capability", "In-house capability"], ["contact", "Contact"]] },
];
const SYSTEMS = [
  ...residentialSystems.map((s) => ({ ...s, page: "residential", path: "/residential" })),
  ...commercialSystems.map((s) => ({ ...s, page: "commercial", path: "/commercial" })),
];

const smooth = () => (window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth");
const match = (q, ...txt) => !q || txt.join(" ").toLowerCase().includes(q);

const Row = ({ to, title, meta, i, onJump, testid }) => (
  <li className="mi-in" style={{ animationDelay: `${Math.min(i, 14) * 28}ms` }}>
    <Link to={to} onClick={onJump} className="menu-row group" data-testid={testid}>
      <span className="min-w-0">
        <span className="block truncate text-[15px] font-semibold text-white">{title}</span>
        {meta && <span className="block truncate text-xs text-[#C9D6E2]">{meta}</span>}
      </span>
      <ChevronRight aria-hidden="true" size={18} className="flex-none text-[#C9D6E2] transition-transform duration-200 group-hover:translate-x-1" />
    </Link>
  </li>
);

const Group = ({ title, action, children, testid }) => (
  <section className="mi-in pt-6 first:pt-2" data-testid={testid}>
    <div className="mb-2 flex items-baseline justify-between gap-3">
      <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C9D6E2]">{title}</h3>
      {action}
    </div>
    <ul className="grid gap-1.5">{children}</ul>
  </section>
);

export const MenuPanel = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { pathname } = useLocation();
  const { openEnquiry } = useEnquiry();
  useEffect(() => { if (open) setQuery(""); }, [open]);
  const q = query.trim().toLowerCase();

  // Same-page targets: close the menu, then glide to the section. Cross-page hash links are handled by ScrollManager.
  const jumpTo = (path, id) => (e) => {
    setOpen(false);
    if (path !== pathname) return;
    e.preventDefault();
    setTimeout(() => {
      const el = id ? document.getElementById(id) : null;
      window.history.replaceState(null, "", id ? `${path}#${id}` : path);
      if (el) el.scrollIntoView({ block: "start", behavior: smooth() });
      else window.scrollTo({ top: 0, behavior: smooth() });
    }, 260);
  };
  const contact = () => { setOpen(false); setTimeout(() => openEnquiry("Navigation menu — Contact"), 120); };

  const groups = useMemo(() => PAGES
    .map((p) => ({ ...p, items: p.sections.filter(([, name]) => match(q, name, p.label)) }))
    .filter((p) => p.items.length || match(q, p.label)), [q]);
  const systems = useMemo(() => SYSTEMS.filter((s) => match(q, s.name, s.series, s.page)), [q]);
  let n = 0;

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger className="menu-trigger" aria-label="Open menu" data-testid="nav-menu-btn"><MenuIcon /></Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="menu-overlay fixed inset-0 z-[60] bg-[#0B1C2D]/50" />
        <Dialog.Content className="menu-panel fixed right-0 top-0 z-[70] flex h-[100dvh] w-full flex-col menu-glass text-white sm:w-[460px]" data-testid="nav-menu-panel" aria-describedby={undefined}>
          <div className="flex h-[68px] flex-none items-center justify-between px-5 md:h-[76px] md:px-7">
            <Dialog.Title className="text-sm font-semibold uppercase tracking-[0.2em] text-[#E3EAF0]">Menu</Dialog.Title>
            <Dialog.Close className="menu-trigger" aria-label="Close menu" data-testid="nav-menu-close-btn"><X aria-hidden="true" size={24} /></Dialog.Close>
          </div>

          <div className="flex-none px-5 md:px-7">
            <label className="menu-search">
              <Search aria-hidden="true" size={18} className="flex-none text-[#C9D6E2]" />
              <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search pages, sections or systems" className="w-full bg-transparent text-[15px] text-white outline-none placeholder:text-[#C9D6E2]" aria-label="Search the site" data-testid="nav-search-input" />
            </label>
          </div>

          <nav aria-label="Main" className="menu-scroll mt-2 flex-1 overflow-y-auto overscroll-contain px-5 pb-6 md:px-7" data-testid="nav-results">
            {!q && (
              <div className="mi-in pt-3" data-testid="nav-pages">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#C9D6E2]">Go to a page</h3>
                <div className="grid grid-cols-3 gap-2">
                  {PAGES.map((p) => (
                    <Link key={p.key} to={p.path} onClick={jumpTo(p.path, null)} aria-current={p.path === pathname ? "page" : undefined} className="menu-page" data-testid={`nav-link-${p.key}`}>{p.label}</Link>
                  ))}
                </div>
              </div>
            )}
            {groups.map((p) => (
              <Group key={p.key} title={`${p.label} page`} testid={`nav-group-${p.key}`}
                action={<Link to={p.path} onClick={jumpTo(p.path, null)} className="text-xs font-semibold text-white underline-offset-4 hover:underline" data-testid={`nav-open-${p.key}`}>Open page</Link>}>
                {p.items.map(([id, name]) => <Row key={id} to={`${p.path}#${id}`} title={name} i={n++} onJump={jumpTo(p.path, id)} testid={`nav-shortcut-${p.key}-${id}`} />)}
              </Group>
            ))}
            {systems.length > 0 && (
              <Group title="Systems" testid="nav-group-systems">
                {systems.map((s) => <Row key={`${s.page}-${s.id}`} to={`${s.path}#system-${s.id}`} title={s.name} meta={`${s.page === "residential" ? "Residential" : "Commercial"} · ${s.series}`} i={n++} onJump={jumpTo(s.path, `system-${s.id}`)} testid={`nav-system-${s.id}`} />)}
              </Group>
            )}
            {!groups.length && !systems.length && (
              <p className="mi-in pt-8 text-center text-[15px] text-[#E3EAF0]" data-testid="nav-empty">No matches for "{query}". Try another word.</p>
            )}
          </nav>

          <div className="flex-none border-t border-white/15 px-5 pt-4 md:px-7" style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}>
            <div className="flex gap-2">
              <button type="button" className="btn-glass btn-on-dark flex-1 justify-center" onClick={contact} data-testid="nav-link-contact"><MessageSquare aria-hidden="true" size={16} /> Contact us</button>
              <a className="btn-glass btn-on-dark btn-icon" href={`tel:+1${company.tel.replace(/-/g, "")}`} aria-label={`Call ${company.tel}`} data-testid="nav-call-btn"><Phone aria-hidden="true" size={18} /></a>
            </div>
            <p className="mt-3 text-xs text-[#D6E0E9]">{company.address.street}, {company.address.city}, {company.address.region} · <a className="hover:text-white" href={`mailto:${company.email}`}>{company.email}</a></p>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
